import React, {
  memo, useCallback, useId, useMemo,
} from 'react'

import {
  Button, Checkbox, DualSlider, Icon, InfoOutlined, Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addPolygonRange, clearPolygon, removePolygonRange, setMaxPolygon, setMinPolygon, setPolygonRanges,
} from 'Pods/SearchPage/Store/actions/polygon'
import { POLYGON_RANGE } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

const MIN_POLYGONS = 0
const MAX_POLYGONS = 1_000_000

export const QUICK_SELECTIONS = [
  { value: POLYGON_RANGE.LOW_POLY, key: 'low_poly' },
  { value: POLYGON_RANGE.MID_POLY, key: 'mid_poly' },
  { value: POLYGON_RANGE.HIGH_POLY, key: 'high_poly' },
]

function PolyCountContent({ hideReset = false, hideTooltip = false }) {
  const t = useTranslate()
  const trackFilterSelect = useTrackFilterSelect()
  // The secondary bar keeps its dropdown content mounted while hidden below 960, so the drawer's
  // copy needs its own ids or the drawer labels would toggle the hidden inputs.
  const idPrefix = `poly-count-${useId().replace(/:/g, '')}`

  const polygonRanges = useSearchPageStore(state => state.polygonRanges)
  const minPolygon = useSearchPageStore(state => state.minPolygon)
  const maxPolygon = useSearchPageStore(state => state.maxPolygon)

  const selectedSet = useMemo(() => new Set(polygonRanges), [polygonRanges])
  const hasSelectedAnyFilters = polygonRanges.length > 0 || minPolygon !== undefined || maxPolygon !== undefined

  // Per CU-86c74u3x2: multiple preset checkboxes may be active simultaneously,
  // but the preset group and the custom slider range are mutually exclusive -
  // activating the slider clears all presets, and activating any preset clears
  // the custom range. Within the preset group, checkboxes are additive.
  const handleSliderChange = useCallback(({ min, max }) => {
    const newMin = min === MIN_POLYGONS ? undefined : min
    const newMax = max === MAX_POLYGONS ? undefined : max
    // Same range token buildSearchParams sends for a custom range.
    if (newMax !== undefined) {
      trackFilterSelect(FILTER_NAME.POLY_COUNT, `range_${newMin || 0}_${newMax}`)
    } else if (newMin !== undefined) {
      trackFilterSelect(FILTER_NAME.POLY_COUNT, `more_than_${newMin}`)
    }
    setMinPolygon(newMin)
    setMaxPolygon(newMax)
    if (newMin !== undefined || newMax !== undefined) {
      setPolygonRanges([])
    }
  }, [trackFilterSelect])

  const handleCheckboxChange = useCallback((value, isChecked) => {
    if (isChecked) {
      trackFilterSelect(FILTER_NAME.POLY_COUNT, value)
      addPolygonRange(value)
      setMinPolygon(undefined)
      setMaxPolygon(undefined)
    } else {
      removePolygonRange(value)
    }
  }, [trackFilterSelect])

  const tooltip = hideTooltip ? undefined : (
    <Tooltip
      content={t('search_page.filters.poly_count.info_tooltip')}
      place="bottom"
      tooltipClassName="poly-count-filter__tooltip"
      containerClass="poly-count-filter__info"
    >
      <span className="poly-count-filter__tooltip-trigger">
        <Icon svgComponent={InfoOutlined} size="small" color="dark" notSpaced />
      </span>
    </Tooltip>
  )

  return (
    <div className="poly-count-filter">
      {/* The info icon describes the whole filter, so it sits beside the options: inside the Low-poly
          <label> it took Triangle's .cgt-checkbox:hover fill. filter-wrapper__content gives the rows
          the File format dropdown's type and spacing. */}
      <div className="poly-count-filter__options">
        <div
          className="poly-count-filter__checkboxes filter-wrapper__content"
          role="group"
          aria-label={t('search_page.filters.poly_count.checkboxes_group_label')}
        >
          {QUICK_SELECTIONS.map(({ value, key }) => (
            <Checkbox
              key={value}
              id={`${idPrefix}-${value}`}
              name={`${idPrefix}-${value}`}
              className="poly-count-filter__option"
              label={(
                <span className="poly-count-filter__label">
                  {t(`search_page.filters.poly_count.${key}_label`)}
                  <span className="poly-count-filter__additions">
                    {t(`search_page.filters.poly_count.${key}_value`)}
                  </span>
                </span>
              )}
              isChecked={selectedSet.has(value)}
              isControlled
              onChange={isChecked => handleCheckboxChange(value, isChecked)}
            />
          ))}
        </div>
        {tooltip}
      </div>
      <DualSlider
        id={`${idPrefix}-slider`}
        name={`${idPrefix}-slider`}
        min={minPolygon ?? MIN_POLYGONS}
        max={maxPolygon ?? MAX_POLYGONS}
        minThreshold={MIN_POLYGONS}
        maxThreshold={MAX_POLYGONS}
        onChange={handleSliderChange}
        size="tiny"
        withSliderOnTop
        withMarkers
        isShortenedValue
        withTextAlignCenter
      />
      {!hideReset && (
        <Button
          type="ghost"
          size="tiny"
          customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
          onClick={clearPolygon}
          disabled={!hasSelectedAnyFilters}
        >
          {t('search_page.filters.poly_count.reset')}
        </Button>
      )}
    </div>
  )
}

PolyCountContent.propTypes = {
  hideReset: bool,
  hideTooltip: bool,
}

export default memo(PolyCountContent)
